import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  ArrowLeftIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

import { useAuth } from '../contexts/AuthContext';
import { listZones, requestZoneAccess, leaveZone, type ZoneSummary } from '../utils/zones';

/**
 * My Zones: self-service browsing and access requests for zones (Keycloak
 * groups). Every zone requires an administrator's approval before
 * membership is granted -- requesting access here only sends a
 * notification, it never grants access on its own. Leaving a zone is
 * self-service and needs no approval since it only reduces access.
 *
 * Admins don't use this page -- they get the consolidated Zone Members /
 * Zone Approvals overview pages instead -- so an admin landing here
 * (e.g. via a direct URL) is redirected there.
 */
const ZonesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (user?.is_admin) {
    return <Navigate to="/settings/iam/zone-members" replace />;
  }

  const [zones, setZones] = useState<ZoneSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [requestedFor, setRequestedFor] = useState<string | null>(null);
  const [busyZone, setBusyZone] = useState<string | null>(null);
  // Non-null while the inline "why do you need this" reason form is open
  // for one zone, keyed by zone name.
  const [requestingReasonFor, setRequestingReasonFor] = useState<string | null>(null);
  const [reasonInput, setReasonInput] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const list = await listZones();
      setZones(list);
    } catch {
      setError('Could not load zones.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleStartRequestAccess = (zoneName: string) => {
    setError('');
    setReasonInput('');
    setRequestingReasonFor(zoneName);
  };

  const handleSubmitRequestAccess = async (e: React.FormEvent, zoneName: string) => {
    e.preventDefault();
    setError('');
    setBusyZone(zoneName);
    try {
      await requestZoneAccess(zoneName, reasonInput);
      setRequestedFor(zoneName);
      setRequestingReasonFor(null);
      setReasonInput('');
      await refresh();
    } catch {
      setError(`Could not send an access request for "${zoneName}".`);
    } finally {
      setBusyZone(null);
    }
  };

  const handleLeave = async (zoneName: string) => {
    setError('');
    setBusyZone(zoneName);
    try {
      await leaveZone(zoneName);
      await refresh();
    } catch {
      setError(`Could not leave "${zoneName}".`);
    } finally {
      setBusyZone(null);
    }
  };

  const yourZones = zones.filter(z => z.is_member);
  const availableZones = zones.filter(z => !z.is_member);

  return (
    <div className="max-w-3xl mx-auto p-6">
      <button
        type="button"
        onClick={() => navigate('/')}
        className="flex items-center space-x-1 mb-4 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 focus:outline-none"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        <span>Back to Dashboard</span>
      </button>
      <div className="flex items-center space-x-3 mb-2">
        <UserGroupIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">My Zones</h1>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
        Zones control which MCP servers you can see. Every zone requires an administrator&apos;s
        approval before you&apos;re added — requesting access here just sends the request, it
        does not grant access by itself.
      </p>

      {error && (
        <div className="flex items-center space-x-2 mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {requestedFor && (
        <div className="flex items-center space-x-2 mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300">
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />
          <span className="text-sm">
            Access request for &quot;{requestedFor}&quot; sent to an administrator.
          </span>
        </div>
      )}

      {loading ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading…</p>
      ) : (
        <>
          <section className="mb-8">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Your zones
            </h2>
            {yourZones.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You&apos;re not a member of any zone yet — request access to one below.
              </p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                {yourZones.map(zone => (
                  <li key={zone.name} className="flex items-center justify-between gap-4 p-4">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {zone.name}
                      </div>
                      {zone.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {zone.description}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleLeave(zone.name)}
                      disabled={busyZone === zone.name}
                      className="flex-shrink-0 px-3 py-1.5 rounded-md text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      Leave
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Available zones
            </h2>
            {availableZones.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No other zones available.
              </p>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-gray-700 rounded-lg border border-gray-200 dark:border-gray-700">
                {availableZones.map(zone => (
                  <li key={zone.name} className="p-4">
                    <div className="flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {zone.name}
                        </div>
                        {zone.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {zone.description}
                          </div>
                        )}
                      </div>
                      {zone.has_pending_request ? (
                        <span
                          className="flex-shrink-0 px-3 py-1.5 rounded-md text-sm font-medium bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-300"
                          title="An administrator still needs to approve this request."
                        >
                          Pending approval
                        </span>
                      ) : (
                        requestingReasonFor !== zone.name && (
                          <button
                            type="button"
                            onClick={() => handleStartRequestAccess(zone.name)}
                            disabled={busyZone === zone.name}
                            className="flex-shrink-0 px-3 py-1.5 rounded-md text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            Request Access
                          </button>
                        )
                      )}
                    </div>

                    {!zone.has_pending_request && requestingReasonFor === zone.name && (
                      <form
                        onSubmit={e => handleSubmitRequestAccess(e, zone.name)}
                        className="mt-3 space-y-2"
                      >
                        <label
                          htmlFor={`reason-${zone.name}`}
                          className="block text-xs font-medium text-gray-600 dark:text-gray-400"
                        >
                          Why do you need access? (optional, shown to the approving administrator)
                        </label>
                        <textarea
                          id={`reason-${zone.name}`}
                          value={reasonInput}
                          onChange={e => setReasonInput(e.target.value)}
                          maxLength={500}
                          rows={2}
                          placeholder="e.g. I need to check on the GitLab MCP server for JAB"
                          className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="flex gap-2">
                          <button
                            type="submit"
                            disabled={busyZone === zone.name}
                            className="px-3 py-1.5 rounded-md text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                          >
                            {busyZone === zone.name ? 'Sending…' : 'Send Request'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setRequestingReasonFor(null)}
                            className="px-3 py-1.5 rounded-md text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default ZonesPage;
